import type { Vec2, Ring } from '../../../geo/types';
import type { Tower } from '../../../data/types';
import type { MeshBuilder, VertexStyle, Frame } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { buildTowerMasonry } from './towerMasonry';
import {
  coneSolid,
  octahedronSolid,
  orientedFace,
  planPoint,
  prismSolid,
  pyramidOverRing,
  ringPoints,
  triangulateRing,
} from '../solids';

/**
 * PLAN amendment 2026-09-26: the Schreierstoren is the one crenellated tower. Proportions
 * are measured on docs/reference/crops/schreierstoren.png: body : parapet with merlons :
 * cone = 460 : 90 : 230 px, cone base 0.76 of the body width, about 16 merlons. On the half-round
 * plan the layout gives it, the parapet and its merlons follow the round side and the roof is a
 * half-cone against the flat back.
 */
const CRENELLATED_TOWER_IDS: ReadonlySet<string> = new Set(['tower-schreierstoren']);

export const CRENELLATION = {
  segments: 32,
  corbel: 0.35,
  corbelDrop: 0.5,
  archRise: 0.42,
  archSteps: 8,
  corbelWidth: 0.45,
  corbelFootDrop: 0.9,
  parapetHeight: 1.0,
  parapetThickness: 0.8,
  merlonHeight: 1.0,
  merlonSpacing: 4.4,
  merlonFill: 0.55,
  minMerlons: 16,
  merlonArcSteps: 2,
  roofRadiusRatio: 0.74,
  roofRiseRatio: 0.42,
  drumHeight: 1.0,
  finialRadius: 0.22,
  finialHeight: 1.6,
  finialKnob: 0.3,
} as const;

export function isCrenellatedTower(tower: Pick<Tower, 'id'>): boolean {
  return CRENELLATED_TOWER_IDS.has(tower.id);
}

function arc(radius: number, from: number, to: number, steps: number): Vec2[] {
  return Array.from({ length: steps + 1 }, (_, i): Vec2 => {
    const a = from + ((to - from) * i) / steps;
    return [Math.cos(a) * radius, Math.sin(a) * radius];
  });
}

/** The part of the round a crenellated tower's walls follow: all of it, or a half-round's front. */
interface Round {
  readonly from: number;
  readonly span: number;
  readonly closed: boolean;
}

function roundOf(tower: Pick<Tower, 'shape'>): Round {
  return tower.shape === 'half-round'
    ? { from: -Math.PI / 2, span: Math.PI, closed: false }
    : { from: 0, span: 2 * Math.PI, closed: true };
}

/** A plan ring of `radius` over `round`; a half-round's flat back lies on u = 0. */
function roundRing(radius: number, round: Round): Ring {
  const steps = Math.round((CRENELLATION.segments * round.span) / (2 * Math.PI));
  return round.closed
    ? ringPoints([0, 0], radius, steps, round.from)
    : arc(radius, round.from, round.from + round.span, steps);
}

/** Plan outline of a crenellated tower's body in its frame. */
export function crenellatedPlanRing(tower: Pick<Tower, 'shape' | 'radius'>): Ring {
  return roundRing(tower.radius, roundOf(tower));
}

/** Closed solid between two concentric plan rings (or ring sectors), bottom optional. */
function annularSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  rings: { readonly outer: Ring; readonly inner: Ring },
  y: readonly [number, number],
  closed: boolean
): void {
  const [y0, y1] = y;
  const shape: Ring = closed ? rings.outer : [...rings.outer, ...[...rings.inner].reverse()];
  const holes = closed ? [rings.inner] : [];
  prismSolid(builder, style, frame, shape, y0, y1, { top: false, bottom: false });
  if (closed) {
    builder.prism(style, frame, [...rings.inner].reverse(), y0, y1, {
      top: false,
      bottom: false,
      sides: true,
    });
  }
  for (const tri of triangulateRing(shape, holes)) {
    orientedFace(
      builder,
      style,
      frame,
      tri.map((q) => planPoint(q[0], y1, q[1])),
      [0, 1, 0]
    );
    orientedFace(
      builder,
      style,
      frame,
      tri.map((q) => planPoint(q[0], y0, q[1])),
      [0, -1, 0]
    );
  }
}

/* An open round starts and ends half a step in, so no merlon hangs past the flat back. */
function buildMerlons(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  radii: { readonly outer: number; readonly inner: number },
  y: readonly [number, number],
  round: Round
): void {
  const c = CRENELLATION;
  const share = round.span / (2 * Math.PI);
  const count = Math.max(
    Math.round(c.minMerlons * share),
    Math.round((round.span * radii.outer) / c.merlonSpacing)
  );
  const step = round.span / count;
  const half = (step * c.merlonFill) / 2;
  for (let k = 0; k < count; k++) {
    const mid = round.from + step * (k + (round.closed ? 0 : 0.5));
    const outer = arc(radii.outer, mid - half, mid + half, c.merlonArcSteps);
    const inner = arc(radii.inner, mid - half, mid + half, c.merlonArcSteps);
    annularSolid(builder, style, frame, { outer, inner }, y, false);
  }
}

/** The painted gallery rests on a repeated round-arch frieze and separate corbels.
 * Its former smooth full belt concealed those water-facing component openings. The
 * bracket width, arch rise and concealed inner depth are reconstruction estimates. */
function corbelledGallery(
  builder: MeshBuilder, style: VertexStyle, frame: Frame, round: Round,
  radii: { readonly outer: number; readonly inner: number }, h: number
): void {
  const c = CRENELLATION;
  const count = Math.round(c.minMerlons * round.span / (2 * Math.PI));
  const step = round.span / count;
  const at = (r: number, angle: number, y: number) => planPoint(r * Math.cos(angle), y, r * Math.sin(angle));
  const top = h + c.parapetHeight;
  const bottom = (part: number) => h - c.corbelDrop
    + c.archRise * Math.sqrt(Math.max(0, 1 - (2 * part - 1) ** 2));
  for (let bay = 0; bay < count; bay++) for (let i = 0; i < c.archSteps; i++) {
    const from = round.from + step * (bay + i / c.archSteps);
    const to = round.from + step * (bay + (i + 1) / c.archSteps);
    const y0 = bottom(i / c.archSteps), y1 = bottom((i + 1) / c.archSteps);
    const oa = at(radii.outer, from, y0), ob = at(radii.outer, to, y1);
    const ia = at(radii.inner, from, y0), ib = at(radii.inner, to, y1);
    const ot0 = at(radii.outer, from, top), ot1 = at(radii.outer, to, top);
    const it0 = at(radii.inner, from, top), it1 = at(radii.inner, to, top);
    const mid = (from + to) / 2;
    orientedFace(builder, style, frame, [oa, ob, ot1, ot0], [Math.cos(mid), 0, -Math.sin(mid)]);
    orientedFace(builder, style, frame, [ib, ia, it0, it1], [-Math.cos(mid), 0, Math.sin(mid)]);
    orientedFace(builder, style, frame, [ot0, ot1, it1, it0], [0, 1, 0]);
    // A curved annular arch soffit is not a planar quad. Separate triangles keep
    // each stored normal equal to its physical winding for a close water-level view.
    orientedFace(builder, style, frame, [oa, ia, ib], [0, -1, 0]);
    orientedFace(builder, style, frame, [oa, ib, ob], [0, -1, 0]);
    if (!round.closed && bay === 0 && i === 0)
      orientedFace(builder, style, frame, [ia, oa, ot0, it0], [Math.sin(from), 0, Math.cos(from)]);
    if (!round.closed && bay === count - 1 && i === c.archSteps - 1)
      orientedFace(builder, style, frame, [ob, ib, it1, ot1], [-Math.sin(to), 0, -Math.cos(to)]);
  }
  const supports = round.closed ? count : count + 1;
  const halfAngle = c.corbelWidth / (2 * radii.outer);
  for (let i = 0; i < supports; i++) {
    const centre = round.from + step * i;
    const from = round.closed ? centre - halfAngle : Math.max(round.from, centre - halfAngle);
    const to = round.closed ? centre + halfAngle : Math.min(round.from + round.span, centre + halfAngle);
    annularSolid(builder, style, frame,
      { outer: arc(radii.outer, from, to, 2), inner: arc(radii.inner, from, to, 2) },
      [h - c.corbelFootDrop, h - c.corbelDrop + 0.02], false);
  }
}

export function buildCrenellatedTower(
  builder: MeshBuilder,
  frame: Frame,
  tower: Tower,
  styles: { readonly wall: VertexStyle; readonly roof: VertexStyle }
): void {
  const c = CRENELLATION;
  const r = tower.radius;
  const h = tower.height;
  const round = roundOf(tower);
  const outerR = r + c.corbel;
  const innerR = r - c.parapetThickness + c.corbel;
  const ring = (radius: number): Ring => roundRing(radius, round);
  buildTowerMasonry(builder, styles.wall, frame, tower, ring(r), h, true);
  const parapetTop = h + c.parapetHeight;
  corbelledGallery(builder, styles.wall, frame, round, { outer: outerR, inner: innerR }, h);
  buildMerlons(
    builder,
    styles.wall,
    frame,
    { outer: outerR, inner: innerR },
    [parapetTop, parapetTop + c.merlonHeight],
    round
  );
  const roofR = c.roofRadiusRatio * outerR;
  const eave = h + c.drumHeight;
  const apex = eave + c.roofRiseRatio * (parapetTop + c.merlonHeight);
  prismSolid(builder, styles.wall, frame, ring(roofR), h, eave, { top: false, bottom: false });
  pyramidOverRing(builder, styles, frame, ring(roofR), eave, { plan: [0, 0], y: apex });
  const finial: VertexStyle = { ...styles.roof, surface: SURFACE.stone };
  const spike = { center: [0, 0] as const, radius: c.finialRadius, segments: 6, phase: 0 };
  const spikeBase = apex - c.finialRadius * ((apex - eave) / roofR) - 0.05;
  coneSolid(builder, finial, frame, spike, spikeBase, apex + c.finialHeight, true);
  octahedronSolid(
    builder,
    finial,
    frame,
    planPoint(0, apex + c.finialHeight * 0.6, 0),
    c.finialKnob
  );
}
